/* app/config.js
Bento Box App Configuration and Settings
*/

import _ from 'underscore';
import * as BentoBoxModels from './models/bento-box.js';
import EventChannel from './events.js';
import * as Utils from './utils.js';
import bentoBoxTemplate from '../templates/content/box.html';
import librarianBoxTemplate from '../templates/content/librarian-box.html';
import boxDisabledTemplate from '../templates/content/box-parts/disabled-message.html';
import boxErrorTemplate from '../templates/content/box-parts/error-message.html';
import boxLoadingTemplate from '../templates/content/box-parts/loading-message.html';
import boxNoResultsTemplate from '../templates/content/box-parts/no-results-message.html';
import newSearchButtonTemplate from '../templates/content/box-parts/new-search-button.html';
import moreResultsButtonTemplate from '../templates/content/box-parts/more-results-button.html';
import resultContainerTemplate from '../templates/content/box-parts/result-container.html';
import librarianHelpMessageTemplate from '../templates/content/box-parts/librarian-help-message.html';
import articleTemplate from '../templates/content/results/article.html';
import booksAndMoreTemplate from '../templates/content/results/books-and-more.html';
import databaseTemplate from '../templates/content/results/database.html';
import journalTemplate from '../templates/content/results/journal.html';
import referenceWorkTemplate from '../templates/content/results/reference-work.html';
import websiteTemplate from '../templates/content/results/website.html';
import guideTemplate from '../templates/content/results/guide.html';
import librarianTemplate from '../templates/content/results/librarian.html';
import digitalLibraryTemplate from '../templates/content/results/digital-library.html';
import courseTemplate from '../templates/content/results/course.html';


// Start with miscellaneous constants.
// Base URL for the report broken link form
const brokenFormBaseUrl = 'https://unt.az1.qualtrics.com/jfe/form/SV_bdw4fydzCuTGPDD';
// Maximum length for various fields (in characters). Fields with
// more than this number of characters get truncated.
const maxSummaryLength = 200;
const maxTitleLength = 200;
// Point at which truncation happens, if the field is longer than maxSummaryLength.
// Note that text is truncated at word boundaries, so it may be a little longer
// than this value in reality.
const summaryTruncatePoint = 175;
const titleTruncatePoint = 175;

// Keep track of whether we're displaying in 1-column, 2-column, or 3-column
// format.
function getWindowCols() {
  if (window.innerWidth < 768) {
    return 'col1'
  }
  if (window.innerWidth < 992) {
    return 'col2'
  }
  return 'col3'
};

let windowCols = getWindowCols();
window.addEventListener('resize', function(event) {
  let new_cols = getWindowCols();
  if (new_cols != windowCols) {
    windowCols = new_cols;
    EventChannel.trigger('changed-columns', windowCols);
  }
});


// Parse URL querystring and provide urlParams object for getting (and making)
// various versions of each possible parameter. This object must be initialized
// on each search.
const urlParams = {
  validKeys: ['q', 'boxes', 'includeAll'],
  q: {
    _getEncoded: function(val) {
      return encodeURIComponent(val).replace(/%20/g, '+');
    },
  },
  boxes: {
    _getParsed: function(val) {
      return val ? val.split(',') : val;
    },
  },
  includeAll: {
    _getParsed: function(val) {
      return Utils.strToBool(val);
    },
    _getQSParam: function(val) {
      return val ? `includeAll=${val}` : 'includeAll';
    },
  },
  initialize: function() {
    let rawSP = new URLSearchParams(window.location.search);
    for (let i=0; i<this.validKeys.length; i++) {
      let key = this.validKeys[i];
      this[key].raw = rawSP.get(key);
      this[key].encoded = this.getEncoded(key);
      this[key].parsed = this.getParsed(key);
    }
  },
  getParsed: function(key, val) {
    let what = this[key];
    val = val === undefined ? what.raw : val;
    if (what._getParsed !== undefined) {
      return what._getParsed(val);
    }
    return val;
  },
  getEncoded: function(key, val) {
    let what = this[key];
    val = val === undefined ? what.raw : val;
    if (val !== null && val !== undefined) {
      if (what._getEncoded !== undefined) {
        return what._getEncoded(val);
      }
      return encodeURIComponent(val);
    }
  },
  getQSParam: function(key, val) {
    let what = this[key];
    val = this.getEncoded(key, val);
    if (val !== null && val !== undefined) {
      if (what._getQSParam !== undefined) {
        return what._getQSParam(val);
      }
      return `${key}=${val}`;
    }
  },
  getQS: function(overrides) {
    let newQPs = [];
    for (let i=0; i<this.validKeys.length; i++) {
      let key = this.validKeys[i];
      let param = this.getQSParam(key, overrides[key]);
      if (param) {
        newQPs.push(param);
      }
    }
    let newQS = newQPs.join('&');
    return newQS ? `?${newQS}` : '';
  }
};


// Function that detects whether this is a mobile device.
// (Adapted from detectmobilebrowsers.com)

function isMobile() {
  const fullRegex = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
  const abbrevRegex = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return fullRegex.test(userAgent) || abbrevRegex.test(userAgent.substr(0,4));
}

/* boxStates -- a one-off object that defines and initializes templates
 * representing different states a bento box might find itself in. The
 * 'box' arg used throughout should be a single 'boxes' entry, like
 * boxes.articles or boxes.guides. */
const boxStates = {
  disabled: {
    infoMessage: (vars, box) => '',
    stateMessage: (vars, box) => box.templates.disabledMessage(vars),
  },
  error: {
    infoMessage: (vars, box) => '',
    stateMessage: (vars, box) => box.templates.errorMessage(vars),
  },
  loading: {
    infoMessage: (vars, box) => '',
    stateMessage: (vars, box) => box.templates.loadingMessage(vars),
  },
  noResults: {
    infoMessage: (vars, box) => '',
    stateMessage: (vars, box) => box.templates.noResultsMessage(vars),
    needsButtons: {
      new: ['main'],
    },
  },
  results: {
    infoMessage: (vars, box) => box.templates.infoMessage(vars),
    stateMessage: (vars, box) => '',
    needsButtons: {
      new: ['header', 'main'],
      more: ['header', 'main'],
    },
  },
  getResultState: function(box) {
    return box.collection.totalCount > 0 ? this.results : this.noResults;
  },
  getStartingState: function(box) {
    return box.searchEnabled ? this.loading : this.disabled;
  },
  makeButtons: function(templates, urls, labels, state) {
    let buttons = {more: {header: '', main: ''}, new: {header: '', main: ''}};
    if (state.needsButtons) {
      for (const bType of ['new', 'more']) {
        if (templates[bType] && urls[bType] && state.needsButtons[bType]) {
          for (const bLoc of state.needsButtons[bType]) {
            buttons[bType][bLoc] = templates[bType](
              {url: urls[bType], label: labels[bType], includeText: bLoc == 'main'}
            )
          }
        }
      }
    }
    return buttons;
  },
}

/* The boxes obj stores configuration data for each of our bento boxes.
 * Note: set searchEnabled to false if you want to disable the AJAX search for a
 * given box, e.g. if whatever component powers that search is down or offline temporarily
 * and you don't want to fill up your logs with errors. */
const boxDefaults = {
  searchEnabled: true,
  fullResultsLabel: '',
  newSearchLabel: '',
  defaultNewSearchUrl: '',
  templates: {
    boxContainer: _.template(bentoBoxTemplate),
    disabledMessage: _.template(boxDisabledTemplate),
    errorMessage: _.template(boxErrorTemplate),
    loadingMessage: _.template(boxLoadingTemplate),
    noResultsMessage: _.template(boxNoResultsTemplate),
    infoMessage: _.template(''),
    newSearchButton: _.template(newSearchButtonTemplate),
    moreResultsButton: _.template(moreResultsButtonTemplate),
    resultContainer: _.template(resultContainerTemplate),
    result: _.template(''),
  },
  state: boxStates.loading,
};

const boxes = {
  articles: _.extend({}, boxDefaults, {
    label: 'Articles',
    code: 'articles',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'articles',
        'url': '/articles/',
        'otherFields': ['bestBet', 'didYouMean']
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: articleTemplate}),
    fullResultsLabel: 'See more results about %QUERY% in the Find Articles system.',
    newSearchLabel: 'Start a new search in the Find Articles system.',
    defaultNewSearchUrl: 'https://untexas.summon.serialssolutions.com/#!/advanced',
  }),
  guides: _.extend({}, boxDefaults, {
    label: 'Guides',
    code: 'guides',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'guides',
        'url': '/guides/',
        'otherFields': ['librarian']
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: guideTemplate}),
    fullResultsLabel: 'See more results about %QUERY% in the Subject &amp; Course ' +
                      'Guides system.',
    newSearchLabel: 'Start a new search in the Subject and Course Guides system.',
    defaultNewSearchUrl: 'https://guides.library.unt.edu',
    defaultFullResultsUrl: 'https://guides.library.unt.edu/srch.php?q=%QUERY%',
  }),
  databases: _.extend({}, boxDefaults, {
    label: 'Databases',
    code: 'databases',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'databases',
        'url': '/databases/'
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: databaseTemplate}),
    fullResultsLabel: 'See more results about %QUERY% in the Databases A-Z list.',
    newSearchLabel: 'Start a new search in the Databases A-Z list.',
    defaultNewSearchUrl: 'https://guides.library.unt.edu/az.php',
    defaultFullResultsUrl: 'https://guides.library.unt.edu/az.php?q=%QUERY%',
  }),
  courses: _.extend({}, boxDefaults, {
    label: 'Course Reserves',
    code: 'courses',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'courses',
        'url': '/courses/',
        'otherFields': ['exactMatches']
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: courseTemplate}),
    fullResultsLabel: 'See more courses results in the Course Reserves system.',
    newSearchLabel: 'Start a new Course Reserves search.',
    defaultNewSearchUrl: 'https://iii.library.unt.edu/search/r',
    defaultFullResultsUrl: 'https://iii.library.unt.edu/search/r?SEARCH=%QUERY%',
  }),
  referenceWorks: _.extend({}, boxDefaults, {
    label: 'Background Materials',
    code: 'referenceWorks',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'reference-works',
        'url': '/reference-works/'
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: referenceWorkTemplate}),
    fullResultsLabel: 'See more results for background materials about %QUERY% ' +
                      'in the Summon system.',
    newSearchLabel: 'Start a new search in the Summon system.',
    defaultNewSearchUrl: 'https://untexas.summon.serialssolutions.com/#!/advanced',
  }),
  journals: _.extend({}, boxDefaults, {
    searchEnabled: true,
    label: 'Journals',
    code: 'journals',
    collection: new BentoBoxModels.collection(
      [], {
        'type': 'journals',
        'url': '/journals/',
        'maxResults': 10
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: journalTemplate}),
    fullResultsLabel: 'See more results for journals about %QUERY% in the Discover Catalog.',
    newSearchLabel: 'Start a new search for Journals in the Discover Catalog.',
    defaultNewSearchUrl: 'https://discover.library.unt.edu/' +
                         '?f%5Bresource_type_facet%5D%5B%5D=journals_periodicals',
    defaultFullResultsUrl: 'https://discover.library.unt.edu/' +
                           '?f%5Bresource_type_facet%5D%5B%5D=journals_periodicals' +
                           '&q=%QUERY%',
  }),
  digitalLibrary: _.extend({}, boxDefaults, {
    label: 'Digital Collections',
    code: 'digitalLibrary',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'digital-library',
        'url': '/digital-library/'
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: digitalLibraryTemplate}),
    fullResultsLabel: 'See more results about %QUERY% from the UNT Digital Collections.',
    newSearchLabel: 'Start a new UNT Digital Collections search.',
    defaultNewSearchUrl: 'https://digital.library.unt.edu',
  }),
  booksAndMore: _.extend({}, boxDefaults, {
    label: 'Books & More',
    code: 'booksAndMore',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'booksAndMore',
        'url': '/books-and-more-solr/'
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {result: booksAndMoreTemplate}),
    fullResultsLabel: 'See more results about %QUERY% in the Discover Catalog of ' +
                      'Books &amp; More.',
    newSearchLabel: 'Start a new search in the Discover Catalog of Books &amp; More.',
    defaultNewSearchUrl: 'https://discover.library.unt.edu',
  }),
  librarian: _.extend({}, boxDefaults, {
    label: 'Need Research Help?',
    code: 'librarian',
    collection: new BentoBoxModels.collection(
      [],
      {
        'type': 'librarian',
        'url': '/librarians/',
        'maxResults': 1
      }
    ),
    templates: _.extend({}, boxDefaults.templates, {
      boxContainer: _.template(librarianBoxTemplate),
      errorMessage: _.template(librarianHelpMessageTemplate),
      infoMessage: _.template(librarianHelpMessageTemplate),
      noResultsMessage: _.template(librarianHelpMessageTemplate),
      resultContainer: _.template('<span></span>'),
      result: librarianTemplate
    }),
  }),

  initResultNavItems: function(whichBoxes) {
    return whichBoxes.map((box) => new Object({
      url: `#${box.code}`,
      label: box.label
    }));
  },

  initDefaultNavItems: function(whichBoxes) {
    return whichBoxes.map((box) => new Object({
      url: box.defaultNewSearchUrl,
      label: box.label
    }));
  },
};

// Boxes we no longer display but may still need components for go here.
const defunctBoxes = {
  website: {
    searchEnabled: true,
    label: 'Library Website',
    code: 'website',
    // collection: new BentoBoxModels.collection([], {'type': 'website', 'url': '/website/'}),
    template: websiteTemplate,
    fullResultsLabel: 'See all library website search results for %QUERY% ...',
    newSearchLabel: 'Start new library website search',
    bottomLinkType: 'fullResults',
    defaultNewSearchUrl: 'https://library.unt.edu/search/google/',
  },
}


// Object to control dynamic display of all bento boxes (including the nav bar and results
// display). Configure the default box order for nav and 1, 2, and 3-column displays via
// 'defaults'. Then you can use the 'do' method and pass an array of box names and an
// 'includeAll' boolean to get the requested box config objects in the order requested.
const boxesDisplay = {
  defaults: {
    nav: [
      boxes.articles, boxes.booksAndMore, boxes.journals, boxes.databases,
      boxes.digitalLibrary, boxes.courses, boxes.guides, boxes.referenceWorks
    ],
    col1: [
      boxes.articles, boxes.booksAndMore, boxes.journals, boxes.databases,
      boxes.digitalLibrary, boxes.courses, boxes.guides, boxes.librarian,
      boxes.referenceWorks
    ],
    col2: [
      boxes.articles, boxes.booksAndMore, boxes.journals, boxes.databases,
      boxes.digitalLibrary, boxes.courses, boxes.librarian, boxes.guides,
      boxes.referenceWorks
    ],
    col3: [
      boxes.articles, boxes.booksAndMore, boxes.journals, boxes.digitalLibrary,
      boxes.databases, boxes.courses, boxes.referenceWorks, boxes.librarian,
      boxes.guides
    ]
  },
  do: function(reqBoxNames, includeAll) {
    let reqBoxes = [];
    let displayBoxes = _.clone(this.defaults);
    if (reqBoxNames) {
      let allBoxes = _.clone(boxes);
      if (includeAll) {
        reqBoxNames = reqBoxNames.concat(_.map(
          this.defaults.col3,
          function(item) { return item.code }
        ));
      }
      for (let i=0; i<reqBoxNames.length; i++) {
        let reqBox = allBoxes[reqBoxNames[i]];
        if (reqBox !== undefined) {
          reqBoxes.push(reqBox);
          delete(allBoxes[reqBoxNames[i]]);
        }
      }
    }
    if (reqBoxes.length) {
      displayBoxes.nav = reqBoxes;
      displayBoxes.col1 = reqBoxes;
      displayBoxes.col2 = reqBoxes;
      displayBoxes.col3 = reqBoxes;
    }
    return displayBoxes;
  }
};


// booksAndMoreMatTypes translates material types to HTML elements (labels, images, etc.)
const booksAndMoreMatTypes = {
  'BOOKS': '<span class="type">Book</span>',
  'EBOOKS': '<span class="type">EBook</span>',
  'MUSIC SCORES': '<span class="type">Music Score</span>'
  // ... etc.
};


// otherSearchesLinks stores data for the links that appear at the very bottom
// of the page, for Other Searches. Note the array of arrays, indicating
// organization into columns.
function getOtherSearchesLinks(query) {
  return [
    [
      {
        url: `https://search.unt.edu/s/search.html?query=${query}` + 
             '&f.Tabs%7Cuni-nt-web=Website&collection=unt~sp-search' +
             '&scope=library.unt.edu,-guides.library.unt.edu,-blogs.library.unt.edu,' +
             '-guides.library.unt.edu,-archives.library.unt.edu,-hoc.library.unt.edu,' +
             '-journals.library.unt.edu,-openbooks.library.unt.edu,-omeka.library.unt.edu',
        label: 'UNT Libraries Website'
      },
      {
        url: 'https://discover.library.unt.edu/' +
             `?f%5Bcollection_facet%5D%5B%5D=Music+Library&q=${query}`,
        label: 'Music Library Collection'
      },
      {
        url: 'https://discover.library.unt.edu/' +
             `?f%5Bcollection_facet%5D%5B%5D=Media+Library&q=${query}`,
        label: 'Media Library Collection'
      },
    ], [
      {
        url: 'https://archives.library.unt.edu/search' +
             `?utf8=%E2%9C%93&op%5B%5D=&q%5B%5D=${query}&commit=Search`,
        label: 'Special Collections'
      },
      {
        url: 'https://untexas.summon.serialssolutions.com/search' +
             `?s.q=${query}&s.fvf%5B%5D=ContentType%2CNewspaper%20Article%2Cf`,
        label: 'Newspaper Articles'
      },
      {
        url: 'https://untexas.summon.serialssolutions.com/search' +
             `?s.q=${query}&s.fvf%5B%5D=ContentType%2CBook%20Review%2Cf`,
        label: 'Book Reviews'
      },
    ], [
      {
        url: 'https://untexas.summon.serialssolutions.com/search' +
             `?s.q=${query}&s.fvf%5B%5D=ContentType%2CMagazine%20Article%2Cf`,
        label: 'Magazine Articles'
      },
      {
        url: 'https://untexas.summon.serialssolutions.com/search' +
             `?s.q=${query}&s.fvf%5B%5D=ContentType%2CBook%20Chapter%2Cf`,
        label: 'Book Chapters'
      },
      {
        url: 'https://untexas.summon.serialssolutions.com/search' +
             `?s.q=${query}&s.fvf%5B%5D=ContentType%2CReport%2Cf`,
        label: 'Reports'
      },
    ], [
      {
        url: `https://researchworks.oclc.org/archivegrid/?q=${query}&ft=1`,
        label: 'ArchiveGrid'
      },
      {
        url: `https://www.worldcat.org/search?q=${query}&qt=worldcat_org_all`,
        label: 'WorldCat'
      },
      {
        url: `https://scholar.google.com/scholar?q=${query}`,
        label: 'Google Scholar'
      }
    ],
  ]
};


export {
  brokenFormBaseUrl,
  isMobile,
  getWindowCols,
  urlParams,
  boxes,
  boxesDisplay,
  boxStates,
  booksAndMoreMatTypes,
  getOtherSearchesLinks as otherSearchesLinks,
  maxSummaryLength,
  summaryTruncatePoint,
  maxTitleLength,
  titleTruncatePoint
};
